// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import { shareIconStyles } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { FC, useEffect } from "react";
import { css } from "styled-components";

export interface ShareViaEmailProps {
    modal?: ModalPresentationProps;
    emailToTextField?: TextFieldProps;
    sendButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const shareViaEmailStyle: ShareViaEmailProps = {
    modal: {
        size: 600,
    },
    cancelButton: {
        variant: "secondary",
    },
};

const styles = shareViaEmailStyle;

interface OwnProps {
    shareModalTitle?: string;
    shareAction: any;
    additionalApiParameter?: object | null;
    disabled?: boolean;
}

type Props = OwnProps;

const StyledForm = getStyledWrapper("form");

const ShareViaEmail: FC<Props> = ({ shareModalTitle, shareAction, additionalApiParameter, disabled = false }) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [emailToError, setEmailToError] = React.useState("");
    const [emailTo, setEmailTo] = React.useState("");
    const toasterContext = React.useContext(ToasterContext);
    const emailFieldMessage = translate("Enter a valid email address");
    const requiredFieldMessage = translate("Required");

    const modalOpenHandler = () => {
        setEmailTo("");
        setEmailToError("");
        setIsSubmitted(false);
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const validateEmailTo = (email: string) => {
        if (!validateEmail(email)) {
            setEmailToError(emailTo ? emailFieldMessage : requiredFieldMessage);
            return false;
        }
        setEmailToError("");
        return true;
    };

    const emailToChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailTo(event.target.value);
    };

    const sendButtonClickHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsSubmitted(true);
        if (!validateEmailTo(emailTo)) {
            return;
        }
        setIsLoading(true);
        shareAction({
            ...additionalApiParameter,
            toEmail: emailTo,
            onError: (erorMessage: any) => {
                toasterContext.addToast({
                    body: erorMessage,
                    messageType: "danger",
                });
                setIsLoading(false);
                modalCloseHandler();
            },
            onSuccess: () => {
                toasterContext.addToast({
                    body: siteMessage("MCMC_Share_Email_Success"),
                    messageType: "success",
                });
                setIsLoading(false);
                modalCloseHandler();
            },
        });
    };

    useEffect(() => {
        validateEmailTo(emailTo);
    }, [emailTo]);

    return (
        <>
            <Link {...shareIconStyles} onClick={modalOpenHandler} disabled={disabled}>
                <VisuallyHidden>{translate("Share")}</VisuallyHidden>
            </Link>
            <Modal
                {...styles.modal}
                headline={shareModalTitle || translate("Share Via Email")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledForm onSubmit={sendButtonClickHandler} noValidate>
                    <TextField
                        label={translate("Email To")}
                        value={emailTo}
                        maxLength={70}
                        error={isSubmitted && emailToError}
                        required
                        onChange={emailToChangeHandler}
                        data-test-selector="shareEntity_emailTo"
                        {...styles.emailToTextField}
                    />
                    <StyledWrapper className="modal-actions">
                        <Button
                            {...styles.sendButton}
                            type="submit"
                            data-test-selector="shareEntity_submit"
                            disabled={isLoading}
                        >
                            <EllipsisSpinner label={translate("Send")} showSpinner={isLoading} />
                        </Button>
                        <Button
                            {...styles.cancelButton}
                            onClick={modalCloseHandler}
                            data-test-selector="shareEntity_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </StyledForm>
            </Modal>
        </>
    );
};

export default ShareViaEmail;
